import React from 'react'

import { useTranslation } from 'react-i18next'

import { FieldLabel } from '@cash-design-system/react'
import {
  isValidBsbAu,
  isValidInstitutiontNumberCa,
  isValidRoutingNumberGb,
  isValidRoutingNumberUsa,
  isValidTransitNumberCa,
} from '@cash-web/shared-util-validation'

import { FormMaskedInput } from '../FormMaskedInput'
export type RoutingNumberProps = {
  name: string
  region: 'US' | 'GB' | 'CA' | 'AU'
  autoFocus?: boolean
}
export const RoutingNumber: React.FC<RoutingNumberProps> = ({ name, region = 'US', ...rest }) => {
  const { t } = useTranslation()

  const regionConfig = {
    AU: {
      format: '###-###',
      validate: isValidBsbAu,
    },
    CA: {
      format: '###',
      validate: isValidInstitutiontNumberCa,
    },
    GB: {
      format: '##-##-##',
      validate: isValidRoutingNumberGb,
    },
    US: {
      format: '#########',
      validate: isValidRoutingNumberUsa,
    },
  }

  const { format, validate } = regionConfig[region]

  const { label, placeholder, requiredText, invalidText } = (() => {
    switch (region) {
      case 'US':
        return {
          label: t('Routing number', { context: 'label for form field asking for routing number' }),
          placeholder: t('123456789', { context: 'placeholder for form field asking for routing number' }),
          requiredText: t('Enter a routing number.', {
            context: 'error message for form field when missing a routing number',
          }),
          invalidText: t('Invalid routing number.', {
            context: 'error message for form field for an invalid routing number',
          }),
        }
      case 'GB':
        return {
          label: t('Sort code', { context: 'label for form field asking for sort code' }),
          placeholder: t('12-34-56', { context: 'placeholder for form field asking for sort code' }),
          requiredText: t('Enter a sort code.', { context: 'error message for form field when missing a sort code' }),
          invalidText: t('Invalid sort code.', { context: 'error message for form field for an invalid sort code' }),
        }
      case 'CA':
        return {
          label: t('Institution number', { context: 'label for form field asking for an institution number' }),
          placeholder: t('000', { context: 'placeholder for form field asking for an institution number' }),
          requiredText: t('Enter an institution number.', {
            context: 'error message for form field when missing an institution number',
          }),
          invalidText: t('Invalid institution number.', {
            context: 'error message for form field for an invalid institution number',
          }),
        }
      case 'AU':
        return {
          label: t('BSB number', { context: 'label for form field asking for a BSB number' }),
          placeholder: t('000-000', { context: 'placeholder for form field asking for a BSB number' }),
          requiredText: t('Enter a BSB number.', { context: 'error message for form field when missing a BSB number' }),
          invalidText: t('Invalid BSB number.', {
            context: 'error message for form field for an invalid BSB number',
          }),
        }
    }
  })()

  return (
    <>
      <FieldLabel label={label}>
        <FormMaskedInput
          {...rest}
          name={`${name}_1`}
          placeholder={placeholder}
          format={format}
          rules={{
            required: {
              value: true,
              message: requiredText,
            },
            validate: value => validate(value) || invalidText,
          }}
        />
      </FieldLabel>
      {region === 'CA' && (
        <FieldLabel label={t('Transit number', { context: 'label for form field asking for a transit number' })}>
          <FormMaskedInput
            {...rest}
            autoFocus={false}
            name={`${name}_2`}
            placeholder={t('00000', { context: 'placeholder for a form field asking for a transit number' })}
            format="#####"
            rules={{
              required: {
                value: true,
                message: t('Enter a transit number.', {
                  context: 'error message for a form field when missing a transit number',
                }),
              },
              validate: value =>
                isValidTransitNumberCa(value) ||
                t('Invalid transit number', {
                  context: 'error message for a form field for an invalid transit number',
                }),
            }}
          />
        </FieldLabel>
      )}
    </>
  )
}
